/**
 * Shapes from docs/onboarding-consignee/API-CONTRACT.md §4. The backend owns
 * the vocabulary; nothing here is renamed on the way in.
 */

export type KodeJenisApi = '' | '01' | '02';
export type KodeStatus = '' | '1' | '2' | '3';
/** 'ceisa' rows are written by the backend when a PIB is accepted (contract §6). Unknown values are tolerated. */
export type ConsigneeSource = 'excel' | 'manual' | 'ceisa' | (string & {});

/** A row of the super-admin global list (API-CONTRACT §9): the consignee plus the PPJK that owns it. */
export interface ConsigneeGlobalDTO extends ConsigneeDTO {
  ppjkNpwp: string;
  /** '' when no CEISA user carries that NPWP. */
  ppjkName: string;
}

export interface ConsigneeDTO {
  [key: string]: unknown;
  id: number;
  /** 16 digits, already normalised by the server. */
  npwp: string;
  nama: string;
  alamat: string;
  /** '' or 13 digits. */
  nib: string;
  kodeJenisApi: KodeJenisApi;
  kodeStatus: KodeStatus;
  source: ConsigneeSource;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
  updatedById: number | null;
}

export interface ConsigneeInput {
  npwp: string;
  nama: string;
  alamat?: string;
  nib?: string;
  kodeJenisApi?: string;
  kodeStatus?: string;
}

export type ImportAction = 'new' | 'updated' | 'unchanged' | 'rejected';

export type ImportChangeField = 'nama' | 'alamat' | 'nib' | 'kodeJenisApi' | 'kodeStatus';

export interface ImportChange {
  field: ImportChangeField;
  old: string;
  new: string;
}

export interface ImportRow {
  [key: string]: unknown;
  /** Excel row number (header row is 1). */
  rowNumber: number;
  action: ImportAction;
  npwp: string;
  nama: string;
  alamat: string;
  nib: string;
  kodeJenisApi: string;
  kodeStatus: string;
  /** Only for `updated`. */
  changes?: ImportChange[];
  /** Only for `rejected`; Indonesian, written for the user. */
  reason?: string;
  warnings?: string[];
}

export interface ImportSummary {
  new: number;
  updated: number;
  unchanged: number;
  rejected: number;
}

export interface ImportPreview {
  fileName: string;
  totalRows: number;
  summary: ImportSummary;
  rows: ImportRow[];
}

export interface ImportResult {
  imported: { new: number; updated: number; unchanged: number };
  rejected: ImportRow[];
}

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  totalCount?: number;
  error?: { code?: string; message?: string };
}

/** Labels the app shows for CEISA codes. Codes stay codes on the wire. */
export const JENIS_API_LABELS: Record<Exclude<KodeJenisApi, ''>, string> = {
  '01': 'API-U',
  '02': 'API-P',
};

export const STATUS_LABELS: Record<Exclude<KodeStatus, ''>, string> = {
  '1': 'MITA',
  '2': 'AEO',
  '3': 'LAINNYA',
};

export const jenisApiLabel = (code: string): string => JENIS_API_LABELS[code as Exclude<KodeJenisApi, ''>] ?? '';
export const statusLabel = (code: string): string => STATUS_LABELS[code as Exclude<KodeStatus, ''>] ?? '';

/** Translation key for a row's source, or null for a value this app does not know. */
export const consigneeSourceKey = (source: string | null | undefined): string | null => {
  const s = (source ?? '').toLowerCase();
  if (s === 'excel') return 'consignee.sourceExcel';
  if (s === 'manual') return 'consignee.sourceManual';
  if (s === 'ceisa') return 'consignee.sourceCeisa';
  return null;
};

/** Source text for display: translated when known, the raw value uppercased otherwise. */
export const consigneeSourceText = (source: string | null | undefined, t: (key: string) => string): string => {
  const key = consigneeSourceKey(source);
  return key ? t(key) : (source ?? '').toUpperCase();
};
